const fmt = (n) => (n ?? 0).toLocaleString("en-IN");
const fmtCompact = (n) => {
  const v = Number(n ?? 0);
  if (v >= 1000) return `${(v / 1000).toFixed(1)}k`;
  return String(v);
};
const fmtPct = (n, digits = 1) => `${Number(n ?? 0).toFixed(digits)}%`;

const TENURE_BAND_ORDER = ["0-5 yrs", "5-10 yrs", "10-15 yrs", "15-20 yrs", "20-25 yrs", ">25 yrs"];

const TENURE_BAR_CLASS = ["", "", "orange", "orange", "red", "red"];

export const buildDeploymentStabilityIndex = (tenureData = null, ageData = null) => {
  const retirement = tenureData?.retirement_risk_pct ?? 0;
  const ageing = ageData?.ageing_risk_pct ?? 0;
  const dsi = Math.round(100 - retirement * 1.2 - ageing * 0.8);
  return Math.min(100, Math.max(0, dsi));
};

export const buildDsiGaugeStyle = (dsi = 0) => ({
  background: `radial-gradient(circle at center, white 0 52%, transparent 53%), conic-gradient(var(--ctex-warning) 0 ${dsi}%, rgba(245,158,11,.14) ${dsi}% 100%)`,
});

export const buildDeploymentHeroCopy = (tenureData = null, ageData = null) => {
  const retirement = tenureData?.retirement_risk_pct ?? 0;
  const ageing = ageData?.ageing_risk_pct ?? 0;
  const newEntrants = tenureData?.new_entrant_pct ?? 0;
  return {
    headline: "Deployment Stability Index",
    narrative: `The workforce is broadly stable, but the dashboard should highlight two operating risks: a senior retirement cohort of ${fmtPct(retirement)} and an ageing-risk group of ${fmtPct(ageing)}.`,
    chips: [
      { label: "Amber Watch", className: "" },
      { label: `${fmtPct(newEntrants)} New Entrants`, className: "blue" },
      { label: `${fmtPct(retirement)} Retirement Risk`, className: "red" },
    ],
    primaryAction:
      "Create a block-wise retirement replacement plan and pair new entrants with experienced teachers for the next academic cycle.",
  };
};

export const buildDeploymentHeroKpis = (tenureData = null, ageData = null) => [
  {
    label: "Average Service Years",
    value: `${tenureData?.avg_tenure ?? "—"} yrs`,
    sub: "Balanced tenure base",
    cardClass: "",
  },
  {
    label: "New Entrants",
    value: fmtPct(tenureData?.new_entrant_pct),
    sub: "≤5 years service",
    cardClass: "",
  },
  {
    label: "Retirement Risk",
    value: fmtPct(tenureData?.retirement_risk_pct),
    sub: ">25 years service",
    cardClass: "warning",
  },
  {
    label: "Ageing Risk",
    value: fmtPct(ageData?.ageing_risk_pct),
    sub: ">55 years age",
    cardClass: "danger",
  },
];

export const buildTenureBars = (tenureData = null) => {
  const distribution = tenureData?.distribution ?? [];
  const ordered = TENURE_BAND_ORDER.map((band, idx) => {
    const row = distribution.find((d) => d.tenure_band === band);
    return {
      band,
      count: row?.count ?? 0,
      barClass: TENURE_BAR_CLASS[idx] ?? "",
    };
  });
  const total = ordered.reduce((s, r) => s + r.count, 0) || 1;
  const max = Math.max(...ordered.map((r) => r.count), 1);
  const maxHeight = 98;
  const axisMax = Math.ceil(max / 5500) * 5500 || 22000;
  const axisTicks = [axisMax, axisMax * 0.75, axisMax * 0.5, axisMax * 0.25, 0].map((v) =>
    v >= 1000 ? `${(v / 1000).toFixed(v % 1000 === 0 ? 0 : 1)}K` : String(v)
  );
  return {
    totalRecords: total,
    axisTicks,
    bars: ordered.map((row) => ({
      ...row,
      display: fmtCompact(row.count),
      heightPct: Math.max(8, Math.round((row.count / max) * maxHeight)),
    })),
  };
};

export const buildRiskComposition = (tenureData = null, ageData = null) => {
  const distribution = tenureData?.distribution ?? [];
  const total = distribution.reduce((s, d) => s + (d.count ?? 0), 0) || 1;
  const midCareer = distribution
    .filter((d) => d.tenure_band === "10-15 yrs" || d.tenure_band === "15-20 yrs")
    .reduce((s, d) => s + (d.count ?? 0), 0);
  const midPct = Math.round((midCareer / total) * 1000) / 10;
  return [
    {
      title: "New Entrant Support",
      pct: tenureData?.new_entrant_pct ?? 0,
      body: "Large early-career cohort needs mentoring, induction and school-level deployment stability.",
      barClass: "green",
    },
    {
      title: "Mid-Career Backbone",
      pct: midPct,
      body: "10–20 year teachers can be used as mentors, trainers and cluster anchors.",
      barClass: "",
    },
    {
      title: "Retirement Exposure",
      pct: tenureData?.retirement_risk_pct ?? 0,
      body: "Build a school-wise replacement list and protect critical subject positions.",
      barClass: "red",
    },
    {
      title: "Ageing Exposure",
      pct: ageData?.ageing_risk_pct ?? 0,
      body: "Monitor teachers above 55 years for deployment continuity and handover planning.",
      barClass: "orange",
    },
  ];
};

export const buildQualityCards = (dataQuality = null) => {
  const dq = dataQuality ?? {};
  const score = dq.data_quality_score ?? 0;
  const scoreHint = score >= 85 ? "Strong compliance base" : score >= 70 ? "Good, needs cleanup" : "Needs attention";
  return [
    {
      label: "Data Quality Score",
      value: fmtPct(score),
      hint: scoreHint,
      cardClass: "blue",
      valueColor: "var(--ctex-primary)",
    },
    {
      label: "Total Records",
      value: fmt(dq.total_records),
      hint: "Teacher records base",
      cardClass: "green",
      valueColor: "var(--ctex-success)",
    },
    {
      label: "Aadhaar Issues",
      value: fmtPct(dq.aadhaar_issues_pct),
      hint: "Identity correction queue",
      cardClass: "orange",
      valueColor: "var(--ctex-warning)",
    },
    {
      label: "Not Completed",
      value: fmtPct(dq.not_completed_pct),
      hint: "Pending completion",
      cardClass: "red",
      valueColor: "var(--ctex-danger)",
    },
    {
      label: "Missing CRR",
      value: fmtPct(dq.missing_crr_pct ?? 0),
      hint: (dq.missing_crr_pct ?? 0) === 0 ? "No CRR gap reported" : "CRR correction needed",
      cardClass: "purple",
      valueColor: "var(--ctex-purple)",
    },
  ];
};

export const buildRiskSegments = (tenureData = null, ageData = null, dataQuality = null) => {
  const retirement = tenureData?.retirement_risk_pct ?? 0;
  const ageing = ageData?.ageing_risk_pct ?? 0;
  const newEntrants = tenureData?.new_entrant_pct ?? 0;
  const aadhaar = dataQuality?.aadhaar_issues_pct ?? 0;
  const incomplete = dataQuality?.not_completed_pct ?? 0;
  return [
    {
      priority: 1,
      cohort: ">25 yrs service",
      signal: `${fmtPct(retirement)} retirement exposure`,
      riskLevel: "High",
      riskClass: "red",
      owner: "District HR",
      action: "Prepare replacement and knowledge transfer plan",
    },
    {
      priority: 2,
      cohort: ">55 yrs age",
      signal: `${fmtPct(ageing)} ageing exposure`,
      riskLevel: "Medium",
      riskClass: "orange",
      owner: "Block Officer",
      action: "Identify critical subject/school continuity risk",
    },
    {
      priority: 3,
      cohort: "0–5 yrs service",
      signal: `${fmtPct(newEntrants)} new entrants`,
      riskLevel: "Medium",
      riskClass: "orange",
      owner: "Training Team",
      action: "Mentoring and induction tracking",
    },
    {
      priority: 4,
      cohort: "Aadhaar issue queue",
      signal: `${fmtPct(aadhaar)} identity issues`,
      riskLevel: "Medium",
      riskClass: "orange",
      owner: "Data Team",
      action: "Run weekly correction and validation sprint",
    },
    {
      priority: 5,
      cohort: "Incomplete records",
      signal: `${fmtPct(incomplete)} not completed`,
      riskLevel: "Low",
      riskClass: "green",
      owner: "School Admin",
      action: "Close missing attributes before monthly freeze",
    },
  ];
};

export const buildDeploymentActions = (tenureData = null) => {
  const retirement = tenureData?.retirement_risk_pct ?? 0;
  return [
    {
      title: "Create Retirement Heatmap",
      body: `Break the ${fmtPct(retirement)} retirement-risk cohort by block, subject and school type.`,
      iconClass: "red",
    },
    {
      title: "Mentor New Entrants",
      body: "Assign experienced teachers from the 10–20 year cohort as cluster mentors.",
      iconClass: "green",
    },
    {
      title: "Close Data Quality Gaps",
      body: "Prioritize Aadhaar issues and incomplete records before executive reporting.",
      iconClass: "orange",
    },
  ];
};
